/**
 * Goomba — the placeholder "Goomba" walker (rendered as a Kenney slime), folded
 * from the old Enemy.ts into the Brief 007 Entity spine. A minimal patroller that
 * walks, falls with gravity, reverses at walls AND at ledges (no ground ahead —
 * the safer choice for arbitrary editor-authored levels), and is stomp-killable.
 *
 * Combat (NEW vs the old placeholder): a stomp from above kills it + bounces the
 * player (no heart cost); any OTHER contact costs the player a heart. The old
 * handleStomps had a "no damage" placeholder on side contact — that is now real.
 *
 * Determinism: floor/abs/min/max only (ESLint-clean under src/engine/).
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const SPEED = 70;
const GRAVITY = 2050;
const TERMINAL = 1200;
/** Seconds the flattened sprite lingers after a stomp before going invisible. */
const SQUASH_SECONDS = 0.4;

export class Goomba implements Entity {
  readonly type = EntityType.GOOMBA;
  body: AABB;
  vx: number;
  vy = 0;
  facing: 1 | -1 = -1;
  readonly spriteFacesLeft = true; // Kenney slime art faces left
  alive = true;
  /** Time-to-live (s) after a stomp, so the squashed sprite shows briefly. */
  squashTimer = 0;
  private walkPhase = 0;

  /**
   * @param cx     spawn center-x (world px)
   * @param feetY  spawn feet-bottom Y (world px)
   * @param props  free-form per-entity props (patrolRange / dir). P1 honours `dir`
   *               for the initial facing; full props tuning is P3. Missing → defaults.
   */
  constructor(cx: number, feetY: number, props?: Record<string, unknown>, w = 56, h = 50) {
    this.body = { x: cx - w / 2, y: feetY - h, w, h };
    // Honour an authored initial direction if present; default left (-1).
    const dir = props?.dir;
    if (dir === 1 || dir === '1' || dir === 'right') this.facing = 1;
    else if (dir === -1 || dir === '-1' || dir === 'left') this.facing = -1;
    this.vx = this.facing * SPEED;
  }

  private get walkFrameA(): boolean { return Math.floor(this.walkPhase / 40) % 2 === 0; }

  update(dt: number, ctx: EntityContext): void {
    if (!this.alive) { this.squashTimer = Math.max(0, this.squashTimer - dt); return; }
    const grid = ctx.grid;

    // gravity + ground
    this.vy = Math.min(this.vy + GRAVITY * dt, TERMINAL);
    if (grid.collideY(this.body, this.vy * dt, false).grounded) this.vy = 0;

    // horizontal patrol; reverse at wall
    if (grid.collideX(this.body, this.vx * dt)) this.reverse();

    // ledge check: if no ground just ahead-below the leading foot, turn around
    const aheadX = this.facing > 0 ? this.body.x + this.body.w + 2 : this.body.x - 2;
    const belowY = this.body.y + this.body.h + 4;
    if (!grid.solidBelowPoint(aheadX, belowY)) this.reverse();

    this.walkPhase += Math.abs(this.vx) * dt;
  }

  onPlayerOverlap(ctx: EntityContext): void {
    const p = ctx.player.body;
    const playerFeet = p.y + p.h;
    // Stomp: player falling AND feet near this entity's top edge → kill + bounce, no
    // heart loss. (Mirrors the old handleStomps rule.) Anything else → a heart hurt.
    // Stomp only when the archetype grants offense (CLASSIC). Under PRECISION (no offense)
    // a would-be stomp falls through to a heart hurt → the enemy is a pure hazard.
    const fallingOnto = ctx.player.vy > 0 && playerFeet - this.body.y < this.body.h * 0.7;
    if (fallingOnto && ctx.offenseEnabled) {
      ctx.killEntity(this);
      ctx.bouncePlayer();
      ctx.playSound('stomp', 0.5); // POLISH_01 #1 — dedicated stomp clip (was 'coin')
    } else {
      ctx.hurtPlayer();
    }
  }

  /** Kill hook called by World/ctx.killEntity — start the squash-show window. */
  onKilled(): void {
    if (!this.alive && this.squashTimer > 0) return;
    this.alive = false;
    this.squashTimer = SQUASH_SECONDS;
    this.vx = 0;
  }

  spriteKey(): string {
    if (!this.alive) return 'goomba_flat';
    if (Math.abs(this.vx) > 1) return this.walkFrameA ? 'goomba_walk_1' : 'goomba_walk_2';
    return 'goomba_rest';
  }

  visible(): boolean { return this.alive || this.squashTimer > 0; }

  private reverse(): void {
    this.facing = this.facing > 0 ? -1 : 1;
    this.vx = this.facing * SPEED;
  }
}
